/**
 * Validate a comma-separated list of emails and/or domains.
 * A single "*" means any email is allowed.
 * Returns an error message, or null if valid.
 */
export const ANY_EMAIL_WILDCARD = '*';

export function validateEmailsOrDomains(emailsOrDomains: string): string | null {
  if (!emailsOrDomains.trim()) {
    return 'Please enter an email or domain';
  }

  const emailOrDomainList = emailsOrDomains
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  if (emailOrDomainList.length === 0) {
    return 'Please enter at least one email or domain';
  }

  if (emailOrDomainList.includes(ANY_EMAIL_WILDCARD)) {
    if (emailOrDomainList.length !== 1) {
      return `"${ANY_EMAIL_WILDCARD}" must be the only value when allowing any email`;
    }
    return null;
  }

  const domainRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const emailRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  for (const item of emailOrDomainList) {
    const isEmail = item.includes('@');
    const isValid = isEmail ? emailRegex.test(item) : domainRegex.test(item);

    if (!isValid) {
      return `Invalid ${isEmail ? 'email' : 'domain'}: ${item}. Please use format like "user@example.com" or "example.com"`;
    }
  }

  return null;
}

/** True when the allowlist means every email is accepted (empty, or a lone "*"). */
export function allowsAnyEmail(emailsOrDomains: string[] | undefined | null): boolean {
  if (!emailsOrDomains || emailsOrDomains.length === 0) return true;
  return emailsOrDomains.length === 1 && emailsOrDomains[0] === ANY_EMAIL_WILDCARD;
}

/** Display label for an OTP policy's allowed emails/domains. */
export function formatAllowedEmails(emailsOrDomains: string[] | undefined | null): string {
  if (allowsAnyEmail(emailsOrDomains)) return 'any email';
  return (emailsOrDomains ?? []).join(', ');
}

export const EMAIL_DOMAINS_HINT =
  'Enter email domains/addresses separated by commas (e.g. "user@example.com" or "example.com"), or "*" alone to allow any email';

export const EMAIL_DOMAINS_PLACEHOLDER =
  'email1@example.com, example.com — or * for any email';

export const PRIVACY_POLICY_URL = 'https://www.trustline.id/privacy-policy';
